import { useCallback, useEffect, useMemo } from "react";
import { SllLesson, SllModule } from "@src/types";
import styles from "./LessonForm.module.scss";
import MaterialCard from "@src/components/materialCard/SllCard";
import { LordIcon } from "@src/components/lord-icon/lord-icon";
import { IconInfo } from "@src/constants";
import { hasArrayValues } from "@src/utils/helpers";
import { Events, useAnalytics, useUser } from "@src/hooks";
import { Tooltip } from "antd";
import { resolveMixpanelStandaloneGrade } from "@src/utils/mixpanelProgramType";

type Props = {
  lesson?: SllLesson;
  handleDownloadMaterial?: (
    content: SllModule,
    lesson?: SllLesson,
    needPrint?: boolean,
  ) => void;
};

function LessonForm(props: Props) {
  const { lesson, handleDownloadMaterial } = props;

  const { trackEvent } = useAnalytics();
  const [user] = useUser();

  useEffect(() => {
    if (lesson && user) {
      trackEvent(Events.actions.lessonMaterials, {
        lesson_name: lesson.name,
        grade: resolveMixpanelStandaloneGrade({
          ageGroup: lesson.ageGroup,
          gradeLevel: lesson.gradeLevel,
        }),
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    }
  }, [user, lesson, trackEvent]);

  const requiredMaterials = useMemo(() => {
    if (!lesson?.materials) return [];
    return lesson?.materials.filter(
      (material) => material?.isRequired === true,
    );
  }, [lesson?.materials]);

  const optionalMaterials = useMemo(() => {
    if (!lesson?.materials) return [];
    return lesson?.materials.filter(
      (material) => material?.isRequired === false,
    );
  }, [lesson?.materials]);

  const requireBorderStyles = useMemo(() => {
    const classes = [styles.materialContents];
    // classes.push(styles.pulseAnimation)
    return classes.join(" ");
  }, []);

  const handleDownloadLessonMaterial = useCallback(
    (content: SllModule) => {
      handleDownloadMaterial?.(content, lesson);
    },
    [lesson, handleDownloadMaterial],
  );

  const handlePrintMaterial = useCallback(
    (content: SllModule) => {
      handleDownloadMaterial?.(content, lesson, true);
    },
    [lesson, handleDownloadMaterial],
  );

  return (
    <div className={styles.lessonContainer}>
      <div className={styles.subView}>
        {hasArrayValues(requiredMaterials) && (
          <div className={requireBorderStyles}>
            <div className={styles.requireTitle}>
              Required
              <Tooltip
                placement="topRight"
                title={
                  <div className={styles.lessonTip}>
                    Required materials are essential to achieving the lesson
                    objectives.
                  </div>
                }
                arrowPointAtCenter
                overlayStyle={{ maxWidth: "329px" }}
                color="#FFFFFF"
              >
                <div>
                  <LordIcon
                    src={IconInfo}
                    isHover={true}
                    colors={{ primary: "#FFB073", secondary: "#FFB073" }}
                    size={20}
                  />
                </div>
              </Tooltip>
            </div>
            <div className={styles.unitsDiv}>
              {requiredMaterials.map((material, index) => {
                return (
                  <MaterialCard
                    key={index}
                    content={material as SllModule}
                    handleDownloadMaterial={handleDownloadLessonMaterial}
                    handlePrintMaterial={handlePrintMaterial}
                  />
                );
              })}
            </div>
          </div>
        )}
        {hasArrayValues(optionalMaterials) && (
          <div className={styles.materialContents}>
            <div className={styles.optionTitle}>
              Optional
              <Tooltip
                placement="topRight"
                title={
                  <div className={styles.lessonTip}>
                    Optional materials are supplementary resources designed to
                    enhance learning & foster a deeper understanding.
                  </div>
                }
                arrowPointAtCenter
                overlayStyle={{ maxWidth: "529px" }}
                color="#FFFFFF"
              >
                <div>
                  <LordIcon
                    src={IconInfo}
                    isHover={true}
                    colors={{ primary: "#6C969A", secondary: "#6C969A" }}
                    size={20}
                  />
                </div>
              </Tooltip>
            </div>
            <div className={styles.unitsDiv}>
              {optionalMaterials.map((material, index) => {
                return (
                  <MaterialCard
                    key={index}
                    content={material as SllModule}
                    handleDownloadMaterial={handleDownloadLessonMaterial}
                    handlePrintMaterial={handlePrintMaterial}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
export default LessonForm;
